/**
 * Shared pieces for the purchasing API modules in this folder.
 *
 * Each domain file (suppliers, orders, receiving, inventory, planning, ...)
 * owns its URLs, its types (mirroring the C# models, camelCased by the API's
 * FirstCharLowerPolicy) and its TanStack Query hooks. Everything goes through
 * the main apiClient, which adds the auth token, X-Connection-Id, X-User-Id
 * and version-slot routing.
 *
 * Keep URLs as plain template literals starting with /api/purchasing: the .NET
 * ClientServerRouteContractTests parse these files and fail CI when a URL
 * has no controller route.
 */
import { isAxiosError } from 'axios';

/** Root of every purchasing query key, so one invalidation can refresh the module. */
export const PURCHASING = 'purchasing' as const;

/**
 * The message the API put on a failed request: ProblemDetails `detail`
 * (GlobalExceptionMiddleware), an `{ error }` body (purchasing filters), or a
 * plain string. Falls back to the given text.
 */
export function apiErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as unknown;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const body = data as { detail?: unknown; error?: unknown; title?: unknown; errors?: Record<string, string[]> };
      if (typeof body.detail === 'string' && body.detail) return body.detail;
      if (typeof body.error === 'string' && body.error) return body.error;
      if (body.errors) {
        const first = Object.values(body.errors).flat()[0];
        if (first) return first;
      }
      if (typeof body.title === 'string' && body.title) return body.title;
    }
    if (err.response?.status === 403) return 'You do not have permission to do that.';
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** ISO date (yyyy-mm-dd) in local time, for date-only query parameters. */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
